import { RouterSpinner } from "@/components/common/RouterSpinner";
import { AuthErrorPage } from "@/components/pages/AuthErrorPage";
import { AuthLayout } from "@/features/auth/components/layout";
import { Toaster, toast } from "@patient-watch/ui/sonner";
import type { NavT } from "@/utils/nav";
import { Analytics } from "@vercel/analytics/react";

import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import ErrorPage from "../components/pages/ErrorPage";
import NotFoundPage from "../components/pages/NotFoundPage";
import useAuthQuery from "../features/auth/api/useAuthQuery";
import { getToastCopy } from "../features/i18n/toastCopy";
import { DemoBanner } from "../features/demo/DemoBanner";
import { StatusBanner } from "../features/status/components/StatusBanner";
import { cn } from "../lib/utils";
import type { Auth } from "../utils/auth";

export const Route = createRootRouteWithContext<{
    queryClient: QueryClient;
    auth: Auth;
    dark: boolean;
    nav: NavT;
}>()({
    component: RootComponent,
    errorComponent: ({ error, reset }) => <ErrorPage error={error} reset={reset} />,
    notFoundComponent: NotFoundPage,
});

function RootComponent() {
    const navigate = useNavigate();
    const [authError, setAuthError] = useState<{
        title: string;
        description: string;
    } | null>(null);
    // Using the auth query allows early sign-in. For example if accepting and invite as a new user
    useAuthQuery();

    const resetAuthError = () => {
        setAuthError(null);
    };

    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash;

            if (hash) {
                const params = new URLSearchParams(hash.substring(1));
                const error = params.get("error");
                const errorCode = params.get("error_code");
                const errorDescription = params.get("error_description");

                console.log("Parsed params:", {
                    error,
                    errorCode,
                    errorDescription,
                });

                if (error) {
                    const copy = getToastCopy();
                    const message =
                        errorCode === "otp_expired"
                            ? copy.invitationLinkExpired
                            : (errorDescription ?? copy.invitationLinkInvalid);
                    toast.error(message);

                    // Strip the error hash so a refresh doesn't replay it.
                    window.history.replaceState(null, "", window.location.pathname + window.location.search);

                    void navigate({ to: "/confirm-account" });
                }
            }
        };

        // Run on mount
        handleHashChange();

        // Listen for hash changes
        window.addEventListener("hashchange", handleHashChange);

        // Cleanup
        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, [navigate]);

    if (authError) {
        return (
            <AuthLayout>
                <AuthErrorPage resetAuthError={resetAuthError} />
            </AuthLayout>
        );
    }

    return (
        <>
            {import.meta.env.PROD && <Analytics />}
            <Toaster duration={isMobile ? 2500 : 4000} expand={true} />
            <RouterSpinner />
            <DemoBanner />
            <StatusBanner />
            <Outlet />
            <div className={cn(window.location.hostname.includes("localhost") ? "inline" : "hidden")}>
                {/* <ReactQueryDevtools
					initialIsOpen={false}
					buttonPosition="bottom-left"
				/>
				<Suspense>
					<TanStackRouterDevtools position="bottom-right" />
				</Suspense> */}
            </div>
        </>
    );
}
